import { useState } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { StageLinkLogo } from "../../components/Branding/StageLinkLogo";
import {
  Box,
  Paper,
  Typography,
  Link,
  Button,
  Stack,
  CircularProgress,
  FormControlLabel,
  IconButton,
  styled,
  Switch,
  useMediaQuery,
  useTheme,
  TextField,
  DialogActions,
  DialogTitle,
  DialogContent,
  Dialog,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { InputAdornment } from "@mui/material";
import { motion } from "framer-motion";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import AppleIcon from "@mui/icons-material/Apple";
import EmailIcon from "@mui/icons-material/Email";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LockIcon from "@mui/icons-material/Lock";
import IconTextField from "../../components/TextField/IconTextField";
import Footer from "../../components/Footer/Footer";

import { registerValidationSchema } from "../Register.util";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import { userType } from "../../constants/user";
import withAuthentication from "../../hoc/withAuthentication";
import CountryPhoneInput from "./CountryPhoneInput";
import {
  Input,
  InputField,
  InputFieldLabel,
  InputIcon,
  InputWithoutIcon,
  InputWrapper,
  SignInMainContainer,
  SignInPaperContainer,
  PasswordToggle,
  GradientButton,
} from "../Auth/SignInDesign";
import { Icons } from "../../components/assets/icon";
import { PhoneAndroid } from "@mui/icons-material";
import { useGoogleSignIn } from "../../services/api/googleAuth.api";

// API Endpoints
const REGISTER_ENDPOINT = "https://api.taasnet.com/api/v1/auth/register";
export const SWITCH_TO_TALENT_ENDPOINT =
  "https://api.taasnet.com/api/v1/users/switch-to-talent";

const DEV_PASS = "only";

/** — Netflix-ish tokens (design only) — */
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  border: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.75)",
  red: "#E50914",
  redHover: "#b20710",
};

/** Social button with dark look */
const SocialButton = styled(Button)(() => ({
  width: 160,
  height: 48,
  borderRadius: 12,
  textTransform: "none",
  fontSize: 15,
  fontWeight: 700,
  fontFamily: '"Lexend", sans-serif',
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 10,
  color: COLORS.text,
  backgroundColor: "rgba(255,255,255,0.06)",
  border: `1px solid ${COLORS.border}`,
  boxShadow: "0 10px 24px rgba(0,0,0,.35)",
  transition: "all .2s ease",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.10)",
    borderColor: COLORS.border,
    boxShadow: "0 16px 36px rgba(0,0,0,.5)",
  },
  "&.Mui-disabled": {
    opacity: 0.6,
  },
}));

const BrandSwitch = styled(Switch)(() => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: COLORS.red,
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: COLORS.red,
  },
}));

function Register() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { handleGoogleSignIn, isLoading: googleLoading } = useGoogleSignIn();
  const [unlocked, setUnlocked] = useState(false);
  const [passValue, setPassValue] = useState("");
  const [err, setErr] = useState("");
  const [showDevPass, setShowDevPass] = useState(false); // ⬅️ added

  const verifyPassword = () =>
    passValue === DEV_PASS ? setUnlocked(true) : setErr("Incorrect password");

  const {
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    values,
  } = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      username: "",
      email: "",
      country_code: "+82",
      phone: "",
      password: "",
      confirmPassword: "",
      contact_preferences: "Email",
      account_type: userType.USER,
    },
    validateOnBlur: false,
    validationSchema: registerValidationSchema,
    onSubmit: async (formValues: any) => {
      setLoading(true);
      try {
        const response = await axios.post(REGISTER_ENDPOINT, formValues);
        const {
          data: { data: userData },
        } = response;

        let talent = null;
        if (formValues.account_type === userType.TALENT) {
          const {
            data: { data: talentInfo },
          } = await axios.post(SWITCH_TO_TALENT_ENDPOINT, null, {
            headers: { Authorization: `Bearer ${userData.access_token}` },
          });
          talent = talentInfo;
        }

        localStorage.setItem("access_token", userData.access_token || "");
        enqueueSnackbar(
          `${
            formValues.account_type === userType.TALENT ? "Talent" : "User"
          } registration Successful`,
          { variant: "success" }
        );

        if (talent && talent.account_type === userType.TALENT) {
          return navigate("/register-talent");
        }
        navigate(`/user/${userData.user.id}`);
      } catch (error: any) {
        enqueueSnackbar(
          error.response?.data.message ||
            "Something went wrong in User registration",
          { variant: "error" }
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const RegisterLayout = (
    <>
      <SignInMainContainer
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: "100vh",
          // cinematic background
          background:
            "radial-gradient(1200px 520px at 10% -10%, rgba(229,9,20,0.13), transparent 60%), linear-gradient(180deg, #0b0b0b 0%, #000 100%)",
          color: COLORS.text,
        }}
      >
        {/* Left hero panel (hidden on phones for focus & space) */}
        {!isMobile && (
          <Box
            flex={1}
            sx={{
              borderRadius: { xs: 0, md: "24px 0 0 24px" },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 4, md: 8 },
              position: "relative",
              background:
                "linear-gradient(180deg, rgba(20,20,20,0.92), rgba(20,20,20,0.82))",
              borderRight: `1px solid ${COLORS.border}`,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <StageLinkLogo />
                <Typography
                  component={motion.div}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  sx={{
                    textAlign: "center",
                    lineHeight: 1.25,
                    fontWeight: 800,
                    fontFamily: "Lexend, sans-serif",
                    mt: 1,
                    fontSize: { md: 16, lg: 18 },
                    color: COLORS.text,
                  }}
                >
                  <Box component="span" sx={{ color: COLORS.red }}>
                    The Real-Time Infrastructure
                  </Box>
                  <br />
                  <Box component="span" sx={{ color: COLORS.textMuted }}>
                    For Global Audience Engagement.
                  </Box>
                </Typography>
              </Box>
            </motion.div>
          </Box>
        )}

        {/* Right form panel */}
        <Box
          flex={1}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 3, sm: 4, md: 8 },
            position: "relative",
          }}
        >
          <SignInPaperContainer
            elevation={0}
            sx={{
              width: { xs: "100%", sm: 520, md: 600 },
              p: { xs: 3, sm: 4, md: 5 },
              maxHeight: "90vh",
              overflowY: "auto",
              borderRadius: 3,
              background:
                "linear-gradient(180deg, rgba(20,20,20,0.92), rgba(20,20,20,0.88))",
              border: `1px solid ${COLORS.border}`,
              boxShadow: "0 24px 60px rgba(0,0,0,.6)",
              color: COLORS.text,
            }}
          >
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: { xs: 22, sm: 24 },
                mb: { xs: 2, sm: 1.5 },
                letterSpacing: "-0.01em",
              }}
            >
              Create Account
            </Typography>

            <form
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <InputField sx={{ width: "100%" }}>
                  <InputFieldLabel sx={{ color: COLORS.textMuted }}>
                    First name
                  </InputFieldLabel>
                  <InputWithoutIcon
                    required
                    name="first_name"
                    type="Text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.first_name}
                    placeholder="First Name"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.06)",
                      color: COLORS.text,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 2,
                      "&::placeholder": { color: COLORS.textMuted },
                    }}
                  />
                </InputField>
                <InputField sx={{ width: "100%" }}>
                  <InputFieldLabel sx={{ color: COLORS.textMuted }}>
                    Last name
                  </InputFieldLabel>
                  <InputWithoutIcon
                    required
                    type="Text"
                    name="last_name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.last_name}
                    placeholder="Last Name"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.06)",
                      color: COLORS.text,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 2,
                      "&::placeholder": { color: COLORS.textMuted },
                    }}
                  />
                </InputField>
              </Stack>

              <InputField>
                <InputFieldLabel sx={{ color: COLORS.textMuted }}>
                  Username
                </InputFieldLabel>
                <InputWithoutIcon
                  required
                  type="Text"
                  name="username"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.username}
                  placeholder="User Name"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.06)",
                    color: COLORS.text,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 2,
                    "&::placeholder": { color: COLORS.textMuted },
                  }}
                />
              </InputField>

              <InputField>
                <InputFieldLabel sx={{ color: COLORS.textMuted }}>
                  Email Address
                </InputFieldLabel>
                <InputWrapper
                  sx={{
                    bgcolor: "rgba(255,255,255,0.06)",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 2,
                  }}
                >
                  <Input
                    name="email"
                    type="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    placeholder="Email Address"
                    sx={{
                      color: COLORS.text,
                      "&::placeholder": { color: COLORS.textMuted },
                    }}
                  />
                  <InputIcon>
                    <Icons.EmailAddress />
                  </InputIcon>
                </InputWrapper>
              </InputField>

              {/* Country Code + Phone Number */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems="stretch"
              >
                <InputField sx={{ flex: { xs: 1, sm: "0 0 28%" } }}>
                  <InputFieldLabel sx={{ color: COLORS.textMuted }}>
                    Country Code
                  </InputFieldLabel>
                  <InputWithoutIcon
                    name="country_code"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.country_code}
                    placeholder="+977"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.06)",
                      color: COLORS.text,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 2,
                      "&::placeholder": { color: COLORS.textMuted },
                    }}
                  />
                </InputField>
                <InputField sx={{ flex: 1 }}>
                  <InputFieldLabel sx={{ color: COLORS.textMuted }}>
                    Phone Number
                  </InputFieldLabel>
                  <InputWrapper
                    sx={{
                      bgcolor: "rgba(255,255,255,0.06)",
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Input
                      name="phone"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.phone}
                      placeholder="Phone Number"
                      sx={{
                        color: COLORS.text,
                        "&::placeholder": { color: COLORS.textMuted },
                      }}
                    />
                    <InputIcon>
                      <PhoneAndroid
                        fontSize="small"
                        sx={{ color: COLORS.text }}
                      />
                    </InputIcon>
                  </InputWrapper>
                </InputField>
              </Stack>

              <InputField>
                <InputFieldLabel sx={{ color: COLORS.textMuted }}>
                  Passsword
                </InputFieldLabel>
                <InputWrapper
                  sx={{
                    bgcolor: "rgba(255,255,255,0.06)",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 2,
                  }}
                >
                  <Input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                    placeholder="Password"
                    sx={{
                      color: COLORS.text,
                      "&::placeholder": { color: COLORS.textMuted },
                    }}
                  />
                  <InputIcon>
                    <Icons.PasswordLock />
                  </InputIcon>
                  <PasswordToggle
                    onClick={() => setShowPassword(!showPassword)}
                    sx={{ color: COLORS.text }}
                  >
                    {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </PasswordToggle>
                </InputWrapper>
                {touched.password && errors.password && (
                  <Typography
                    variant="caption"
                    sx={{ color: COLORS.red, textAlign: "left", mt: 0.5 }}
                  >
                    {errors.password}
                  </Typography>
                )}
              </InputField>

              <InputField>
                <InputFieldLabel sx={{ color: COLORS.textMuted }}>
                  Confirm Password
                </InputFieldLabel>
                <InputWrapper
                  sx={{
                    bgcolor: "rgba(255,255,255,0.06)",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 2,
                  }}
                >
                  <Input
                    name="confirmPassword"
                    type={showConfirm ? "text" : "password"}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.confirmPassword}
                    placeholder="Confirm Password"
                    sx={{
                      color: COLORS.text,
                      "&::placeholder": { color: COLORS.textMuted },
                    }}
                  />
                  <InputIcon>
                    <Icons.PasswordLock />
                  </InputIcon>
                  <PasswordToggle
                    onClick={() => setShowConfirm(!showConfirm)}
                    sx={{ color: COLORS.text }}
                  >
                    {showConfirm ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </PasswordToggle>
                </InputWrapper>
                {touched.confirmPassword && errors.confirmPassword && (
                  <Typography
                    variant="caption"
                    sx={{ color: COLORS.red, textAlign: "left", mt: 0.5 }}
                  >
                    {errors.confirmPassword}
                  </Typography>
                )}
              </InputField>

              {/* Talent switch */}
              <FormControlLabel
                control={
                  <BrandSwitch
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue(
                        "account_type",
                        e.target.checked ? userType.TALENT : userType.USER
                      )
                    }
                    checked={values.account_type === userType.TALENT}
                  />
                }
                label="Sign Up as a Talent"
                sx={{
                  alignSelf: "flex-start",
                  color: COLORS.text,
                  fontWeight: 600,
                }}
              />

              {/* Submit */}
              <GradientButton
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  fontWeight: 900,
                  py: 1.25,
                  boxShadow: "0 12px 30px rgba(229,9,20,.35)",
                  background: `${COLORS.red} !important`,
                  "&:hover": { background: `${COLORS.redHover} !important` },
                }}
              >
                {loading ? (
                  <CircularProgress size="24px" color="inherit" />
                ) : (
                  "Sign Up"
                )}
              </GradientButton>
            </form>

            {/* Social Logins */}
            <Typography
              variant="body2"
              sx={{ mt: 3, color: COLORS.textMuted, textAlign: "center" }}
            >
              Or continue with
            </Typography>

            <Stack
              direction="row"
              flexWrap="wrap"
              spacing={{ xs: 1, sm: 2 }}
              sx={{
                mt: { xs: 1.5, sm: 2.5 },
                width: "100%",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 1, sm: 2 },
              }}
            >
              <SocialButton
                onClick={handleGoogleSignIn}
                disabled={googleLoading || loading}
              >
                {googleLoading ? (
                  <CircularProgress size={20} sx={{ color: "inherit" }} />
                ) : (
                  <Icons.Google width="24" height="24" />
                )}
              </SocialButton>

              <SocialButton>
                <img
                  src="/assets/kakaotalk.png"
                  alt="Kakao"
                  style={{ width: 24, height: 24, objectFit: "contain" }}
                />
              </SocialButton>

              <SocialButton>
                <AppleIcon sx={{ color: COLORS.text, fontSize: 24 }} />
              </SocialButton>
            </Stack>

            {/* Terms & Privacy */}
            <Typography
              variant="body2"
              sx={{
                mt: { xs: 2, sm: 3 },
                color: COLORS.textMuted,
                textAlign: "center",
              }}
            >
              By creating an account you agree to Stagelink's{" "}
              <Link
                href="https://stagelink.live/terms"
                underline="hover"
                sx={{ color: "#93c5fd" }}
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                href="https://stagelink.live/privacy"
                underline="hover"
                sx={{ color: "#93c5fd" }}
              >
                Privacy Policy
              </Link>
              .
            </Typography>

            {/* Already Have an Account? */}
            <Typography
              variant="body2"
              sx={{
                mt: { xs: 2, sm: 3 },
                color: COLORS.textMuted,
                textAlign: "center",
              }}
            >
              Already have an account?{" "}
              <Link href="/sign-in" underline="hover" sx={{ color: "#93c5fd" }}>
                Log In
              </Link>
            </Typography>
          </SignInPaperContainer>
        </Box>
      </SignInMainContainer>
      <Footer />
    </>
  );

  return (
    <>
      {/* developer password dialog — unchanged logic, Netflix styling */}
      {!unlocked && (
        <Dialog
          open
          fullWidth
          maxWidth="xs"
          onClose={() => navigate(-1)} // ⬅️ close/cancel navigates back
          PaperProps={{
            sx: {
              borderRadius: 2,
              bgcolor: COLORS.panel,
              color: COLORS.text,
              border: `1px solid ${COLORS.border}`,
              boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
              backgroundImage:
                "linear-gradient(180deg, rgba(10,10,10,0.95), rgba(10,10,10,0.85))",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontFamily: "Lexend",
              fontWeight: 800,
              letterSpacing: "-0.01em",
              color: COLORS.text,
              pb: 1,
            }}
          >
            Developer Access
            <IconButton
              size="small"
              onClick={() => navigate(-1)} // ⬅️ close icon = back
              sx={{ color: COLORS.textMuted }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ fontFamily: "Lexend" }}>
            <Typography variant="body2" mb={2} sx={{ color: COLORS.textMuted }}>
              This environment is restricted. Enter the developer password.
            </Typography>

            <TextField
              fullWidth
              autoFocus
              label="Password"
              type={showDevPass ? "text" : "password"} // ⬅️ visibility toggle
              value={passValue}
              error={!!err}
              helperText={err}
              onChange={(e) => {
                setErr("");
                setPassValue(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") verifyPassword();
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setShowDevPass((s) => !s)}
                      sx={{ color: COLORS.textMuted }}
                      aria-label={
                        showDevPass ? "Hide password" : "Show password"
                      }
                    >
                      {showDevPass ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: COLORS.bg,
                  color: COLORS.text,
                  borderRadius: 1.5,
                  "& fieldset": { borderColor: COLORS.border },
                  "&:hover fieldset": { borderColor: COLORS.textMuted },
                  "&.Mui-focused fieldset": { borderColor: COLORS.red },
                },
                "& .MuiInputLabel-root": {
                  color: COLORS.textMuted,
                  "&.Mui-focused": { color: COLORS.text },
                },
                "& .MuiFormHelperText-root": {
                  color: "#ff6b6b",
                },
              }}
            />
          </DialogContent>

          <DialogActions sx={{ pr: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => navigate(-1)} // ⬅️ Cancel navigates to previous page
              sx={{
                color: COLORS.text,
                textTransform: "none",
                fontWeight: 700,
                borderColor: COLORS.border,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={verifyPassword}
              variant="contained"
              sx={{
                fontFamily: "Lexend",
                fontWeight: 800,
                px: 3,
                borderRadius: 1.5,
                bgcolor: COLORS.red,
                boxShadow: "0 10px 24px rgba(229,9,20,0.35)",
                textTransform: "none",
                "&:hover": { bgcolor: COLORS.redHover },
              }}
            >
              Enter
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* actual register UI */}
      {unlocked && RegisterLayout}
    </>
  );
}

export default withAuthentication(Register, ["/register"]);
